<template>
  <!--
    Single production card for the homepage grid.
    Layout (top to bottom): 16:9 image, hotel name, date range, marketing remark
    (clamped to 3 lines), price, and a הזמן CTA. When the production is part of
    a multi-hotel package (`packagePeerCount > 1`), a secondary
    "מלונות נוספים (N-1)" button opens the ProductionPackageDialog to compare
    the other hotels in the same package.
  -->
  <div class="production-card">
    <div class="production-card__image-wrap">
      <img
        v-if="production.imageUrl"
        :src="production.imageUrl"
        :alt="production.hotelName"
        class="production-card__image"
        loading="lazy"
      />
      <div v-else class="production-card__image production-card__image--placeholder"></div>
    </div>

    <div class="production-card__body">
      <h3 class="production-card__name">{{ production.hotelName }}</h3>
      <div class="production-card__meta">
        <span class="production-card__dates">{{ formattedDates }}</span>
        <span class="production-card__dot">·</span>
        <span class="production-card__nights">{{ production.nights }} לילות</span>
      </div>

      <p class="production-card__remark">{{ production.remark }}</p>

      <div class="production-card__price-row">
        <span class="production-card__price">₪{{ formattedPrice }}</span>
        <span class="production-card__price-hint">לחדר לשהות</span>
      </div>

      <div class="production-card__actions">
        <button class="production-card__cta" @click="onBook">הזמן</button>
        <button
          v-if="packagePeerCount > 1"
          class="production-card__peer-btn"
          @click="$emit('open-package')"
        >
          מלונות נוספים ({{ packagePeerCount - 1 }})
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductionCard',
  props: {
    // A single item from the productions API — see the API doc for shape.
    production: { type: Object, required: true },
    // How many other hotels share this production's packageKey. 1 means this is
    // a solo production (no peer button). >1 shows the "מלונות נוספים" CTA.
    packagePeerCount: { type: Number, default: 1 },
  },
  computed: {
    formattedDates() {
      // API sends yyyy-MM-dd; display as dd/MM-dd/MM.
      const inD = this.parseDate(this.production.checkin);
      const outD = this.parseDate(this.production.checkout);
      if (!inD || !outD) return '';
      return `${this.pad(inD.d)}/${this.pad(inD.m)}-${this.pad(outD.d)}/${this.pad(outD.m)}`;
    },
    formattedPrice() {
      const n = Number(this.production.totalPrice) || 0;
      return n.toLocaleString('he-IL');
    },
  },
  methods: {
    parseDate(iso) {
      if (!iso || typeof iso !== 'string') return null;
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return null;
      return { y: m[1], m: m[2], d: m[3] };
    },
    pad(s) { return String(s).padStart(2, '0'); },
    onBook() {
      this.$emit('book', this.production);
    },
  },
};
</script>

<style scoped>
.production-card {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e6ebf1;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(31, 76, 150, 0.06);
  overflow: hidden;
  transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s;
}
.production-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(31, 76, 150, 0.12);
  border-color: #b8ceea;
}

.production-card__image-wrap {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #eef2f7;
  overflow: hidden;
}
.production-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.production-card__image--placeholder {
  background: linear-gradient(135deg, #d7dee7 0%, #e6ebf1 100%);
}

.production-card__body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.production-card__name {
  font-size: 16px;
  font-weight: 700;
  color: #1a3a5c;
  margin: 0;
  line-height: 1.3;
}

.production-card__meta {
  font-size: 12px;
  color: #6b7d95;
  display: flex;
  align-items: center;
  gap: 6px;
}
.production-card__dot { color: #b8c6da; }

.production-card__remark {
  font-size: 13px;
  color: #4a5a6e;
  line-height: 1.5;
  margin: 4px 0 0;
  /* Clamp to 3 lines with ellipsis so cards keep a uniform height. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.production-card__price-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: auto;
  padding-top: 4px;
}
.production-card__price {
  font-size: 20px;
  font-weight: 800;
  color: #1f4c96;
  font-variant-numeric: tabular-nums;
}
.production-card__price-hint {
  font-size: 12px;
  color: #6b7d95;
}

.production-card__actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.production-card__cta,
.production-card__peer-btn {
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.production-card__cta {
  background: #f3a75a;
  color: #ffffff;
  border-color: #f3a75a;
  flex: 1;
}
.production-card__cta:hover { background: #e08d3f; border-color: #e08d3f; }

.production-card__peer-btn {
  background: transparent;
  color: #1f4c96;
  border-color: #1f4c96;
}
.production-card__peer-btn:hover { background: #eef4fb; }
</style>
